import React, { useState, useEffect } from 'react';
import { ActivityData, dashboardApi } from '@/lib/services/dashboard-api';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, ArrowRight, Clock, User, AlertTriangle, Shield, MessageCircle, Play, Settings } from 'lucide-react';
import { formatDistanceToNowSafe } from '@/lib/utils/date';

interface RecentActivitiesListProps {
  onViewMore?: () => void;
  maxActivities?: number;
}

export function RecentActivitiesList({ onViewMore, maxActivities = 5 }: RecentActivitiesListProps) {
  const [activities, setActivities] = useState<ActivityData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await dashboardApi.getRecentActivities(maxActivities);
      setActivities(data);
    } catch (err) {
      console.error('Error fetching recent activities:', err);
      setError('Failed to load recent activities');
    } finally {
      setIsLoading(false);
    }
  };

  const getActivityIcon = (eventType: string) => {
    switch (eventType.toLowerCase()) {
      case 'user_login':
      case 'user_logout':
        return <User className="h-4 w-4" />;
      case 'stream_created':
      case 'stream_started':
      case 'stream_ended':
        return <Play className="h-4 w-4" />;
      case 'comment_posted':
      case 'comment_moderated':
        return <MessageCircle className="h-4 w-4" />;
      case 'user_banned':
      case 'user_restricted':
        return <Shield className="h-4 w-4" />;
      case 'system_error':
      case 'moderation_flagged':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 hover:bg-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'low':
      default:
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
    }
  };

  const formatActivityAction = (activity: ActivityData) => {
    const eventType = activity.eventType.toLowerCase();

    if (eventType.includes('login')) return 'logged in';
    if (eventType.includes('logout')) return 'logged out';
    if (eventType.includes('stream_created')) return 'created stream';
    if (eventType.includes('stream_started')) return 'started stream';
    if (eventType.includes('stream_ended')) return 'ended stream';
    if (eventType.includes('comment_posted')) return 'posted comment';
    if (eventType.includes('comment_moderated')) return 'moderated comment';
    if (eventType.includes('user_banned')) return 'banned user';
    if (eventType.includes('user_restricted')) return 'restricted user';
    if (eventType.includes('system_error')) return 'system error';
    if (eventType.includes('moderation_flagged')) return 'flagged content';

    return activity.action || 'performed action';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(maxActivities)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-gray-300 rounded"></div>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRecentActivities}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-6">
            <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex-shrink-0 w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  {getActivityIcon(activity.eventType)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src="" alt={activity.user?.name || 'Unknown'} />
                      <AvatarFallback className="text-xs">
                        {activity.user ? getInitials(activity.user.name) : '?'}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-sm font-medium truncate">
                      {activity.user?.name || 'System'}
                    </p>
                    {activity.severity && (
                      <Badge
                        variant="secondary"
                        className={`text-xs px-1.5 py-0.5 ${getSeverityColor(activity.severity)}`}
                      >
                        {activity.severity}
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-1">
                    {formatActivityAction(activity)}
                    {activity.details && (
                      <span className="ml-1 text-xs">• {activity.details}</span>
                    )}
                  </p>

                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNowSafe(activity.timestamp)}
                  </div>
                </div>
              </div>
            ))}

            {onViewMore && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onViewMore}
                  className="w-full justify-between"
                >
                  View Full Logs
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
